import { carDocumentsService } from '@/api/car-documents.service';
import type {
  CarDocument,
  CreateCarDocumentDto,
  UpdateCarDocumentDto,
} from '@/entities/cars/car-document.entity';
import type { FindAllCarDocumentsQuery } from '@/entities/cars/find-all-car-documents-query';
import type { CarDocumentType } from '@/entities/cars/car-document-type.enum';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MutationDefaultsContext } from './query-client';
import {
  addItemToQueriesData,
  removeItemFromQueriesData,
  updateItemOnQueriesData,
} from '@/utils/update-query-data';

export const CAR_DOCUMENTS_QUERY_KEY = 'car-documents';
const QUERY_KEY = CAR_DOCUMENTS_QUERY_KEY;

/**
 * Fetches the single most-recently-created document of a given type for a car.
 * Returns `null` when no such document exists.
 */
export const useLatestCarDocumentByType = (carId: string, type: CarDocumentType) => {
  return useQuery({
    queryKey: [QUERY_KEY, carId, 'latest', type],
    queryFn: async () => {
      const page = await carDocumentsService.getAllPaginated(carId, { type: [type], limit: 1 });
      return page.items?.[0] ?? null;
    },
  });
};

export const useCarDocument = (carId: string, documentId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: [QUERY_KEY, carId, documentId],
    queryFn: () => carDocumentsService.getOne(carId, documentId),
    enabled,
  });
};

export const useCarDocuments = (carId: string) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY],
    queryFn: ({ pageParam = 1 }) => carDocumentsService.getAllPaginated(carId, { page: pageParam }),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useCarDocumentsWithQuery = (carId: string, query: FindAllCarDocumentsQuery) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY],
    queryFn: ({ pageParam = 1 }) =>
      carDocumentsService.getAllPaginated(carId, { ...query, page: pageParam }),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useUpdateCarDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({
      carId,
      documentId,
      document,
    }: {
      carId: string;
      documentId: string;
      document: UpdateCarDocumentDto;
    }) => carDocumentsService.update(carId, documentId, document),
    onMutate: () => ({
      successMessage: 'Car Document updated',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      updateItemOnQueriesData<CarDocument>([QUERY_KEY], result, queryClient);
    },
  });
};

export const useRemoveCarDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({ carId, documentId }: { carId: string; documentId: string }) =>
      carDocumentsService.remove(carId, documentId),
    onMutate: (ids) => ({ ...ids, showSuccessToast: false }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      removeItemFromQueriesData<CarDocument>([QUERY_KEY], variables?.documentId, queryClient);
    },
  });
};

export const useCreateCarDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({ carId, document }: { carId: string; document: CreateCarDocumentDto }) =>
      carDocumentsService.create(carId, document),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Car Document created',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      addItemToQueriesData<CarDocument>([QUERY_KEY], result, queryClient);
    },
  });
};
